import React from "react";
import {
  FaPlus,
  FaPlaneDeparture,
  FaFileContract,
  FaTasks,
  FaQuestionCircle,
  FaDollarSign,
  FaCalendarCheck,
} from "react-icons/fa";

const CalendarMonthView = ({ currentDate, events, onSelectEvent, onAddEventForDate }) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // First day of current month
  const firstDay = new Date(year, month, 1);
  const startingDayOfWeek = firstDay.getDay(); // 0 (Sun) to 6 (Sat)
  // Total days in month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Days in previous month
  const prevMonthDays = new Date(year, month, 0).getDate();

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Helper to format YYYY-MM-DD
  const formatDateKey = (d) => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const todayStr = formatDateKey(new Date());

  // Map events by date key
  const eventsByDate = {};
  events.forEach((evt) => {
    if (!evt.start_time) return;
    const dateObj = new Date(evt.start_time);
    if (isNaN(dateObj.getTime())) return;
    const key = formatDateKey(dateObj);
    if (!eventsByDate[key]) eventsByDate[key] = [];
    eventsByDate[key].push(evt);
  });

  const cells = [];

  // Previous month trailing days
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    const dayNum = prevMonthDays - i;
    const d = new Date(year, month - 1, dayNum);
    const key = formatDateKey(d);
    cells.push({
      date: d,
      dayNum,
      isOtherMonth: true,
      isToday: key === todayStr,
      dateKey: key,
    });
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(year, month, i);
    const key = formatDateKey(d);
    cells.push({
      date: d,
      dayNum: i,
      isOtherMonth: false,
      isToday: key === todayStr,
      dateKey: key,
    });
  }

  // Next month leading days to complete grid (up to multiple of 7)
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      const key = formatDateKey(d);
      cells.push({
        date: d,
        dayNum: i,
        isOtherMonth: true,
        isToday: key === todayStr,
        dateKey: key,
      });
    }
  }

  const getEventBadgeClass = (evt) => {
    const type = (evt.event_type || "").toLowerCase();
    const source = (evt.module_source || "").toLowerCase();

    if (source === "sales" || type.includes("flight")) return "badge-flight";
    if (source === "documents" || type.includes("expiry")) return "badge-expiry";
    if (source === "tasks" || type.includes("task")) return "badge-task";
    if (source === "inquiries" || type.includes("follow")) return "badge-followup";
    if (source === "invoices" || type.includes("payment")) return "badge-payment";
    return "badge-meeting";
  };

  const getEventIcon = (evt) => {
    const type = (evt.event_type || "").toLowerCase();
    const source = (evt.module_source || "").toLowerCase();

    if (source === "sales" || type.includes("flight")) return <FaPlaneDeparture style={{ fontSize: 9 }} />;
    if (source === "documents" || type.includes("expiry")) return <FaFileContract style={{ fontSize: 9 }} />;
    if (source === "tasks" || type.includes("task")) return <FaTasks style={{ fontSize: 9 }} />;
    if (source === "inquiries" || type.includes("follow")) return <FaQuestionCircle style={{ fontSize: 9 }} />;
    if (source === "invoices" || type.includes("payment")) return <FaDollarSign style={{ fontSize: 9 }} />;
    return <FaCalendarCheck style={{ fontSize: 9 }} />;
  };

  const formatEventTime = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="month-calendar-grid-wrapper">
      <div className="weekday-header-row">
        {weekdays.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div className="month-days-grid">
        {cells.map((cell, idx) => {
          const dayEvents = eventsByDate[cell.dateKey] || [];
          const visibleEvents = dayEvents.slice(0, 3);
          const extraCount = dayEvents.length - visibleEvents.length;

          return (
            <div
              key={idx}
              className={`calendar-day-cell ${cell.isOtherMonth ? "other-month" : ""} ${
                cell.isToday ? "is-today" : ""
              }`}
            >
              <div className="calendar-day-header">
                <span className="day-number">{cell.dayNum}</span>
                <button
                  type="button"
                  className="day-add-btn"
                  title="Schedule on this day"
                  onClick={() => onAddEventForDate(cell.date)}
                >
                  <FaPlus />
                </button>
              </div>

              <div className="day-events-container">
                {visibleEvents.map((evt) => (
                  <div
                    key={evt.id}
                    className={`month-event-pill ${getEventBadgeClass(evt)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectEvent(evt);
                    }}
                    title={`${evt.title} (${evt.event_type})`}
                  >
                    {getEventIcon(evt)}
                    {!evt.is_all_day && (
                      <span className="month-event-time">
                        {formatEventTime(evt.start_time)}
                      </span>
                    )}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {evt.title}
                    </span>
                  </div>
                ))}

                {extraCount > 0 && (
                  <div
                    className="more-events-indicator"
                    onClick={() => onSelectEvent(dayEvents[3])}
                  >
                    +{extraCount} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarMonthView;
